import { useSyncExternalStore } from "react"
import { useStaticFeature } from "hooks/use-feature"
import { request } from "react-rb/request"

const OPT_IN_URL = "/vertical-navigation/opt-in"

// localStorage values. We only ever show one tooltip at a time, and which one
// (old-nav vs new-nav) is derived from whether the creator is currently on the
// vertical nav, so a single "show-tooltip" state is enough.
export const SHOW_TOOLTIP = "show-tooltip"
export const DONE = "done"

const storageKey = () => `vertical-nav-optin:${window.app?.user?.account_id}`

// Back the state with a shared external store rather than per-component state:
// the modal, the old nav, and the new nav each call useVerticalNavOptIn(), and
// a choice made in one (e.g. "stay on the old nav") must update the others
// immediately — not only on the next full page load.
const listeners = new Set()

const readState = () => {
  try {
    return window.localStorage.getItem(storageKey())
  } catch {
    return null
  }
}

const writeState = value => {
  try {
    window.localStorage.setItem(storageKey(), value)
  } catch {
    // Ignore write failures (e.g. storage disabled); the experience still works
    // for this page load.
  }
  listeners.forEach(listener => listener())
}

const subscribe = listener => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Drives the vertical navigation opt-in experience: the greeting modal, the
 * menu buttons to switch navs, and (in a later change) the guidance tooltips.
 *
 * State machine, persisted in localStorage and scoped to the account:
 *   null         -> eligible for the greeting modal
 *   "show-tooltip" -> the creator has made a choice; show the relevant tooltip
 *   "done"       -> nothing left to show
 */
export function useVerticalNavOptIn() {
  const optInEnabled = useStaticFeature("vertical_navigation_optin").enabled
  const onVerticalNav = useStaticFeature("vertical_navigation").enabled
  const state = useSyncExternalStore(subscribe, readState, () => null)
  const setState = writeState

  const shouldShowModal = optInEnabled && !onVerticalNav && state == null
  const shouldShowOldNavTooltip =
    optInEnabled && !onVerticalNav && state === SHOW_TOOLTIP
  const shouldShowNewNavTooltip =
    optInEnabled && onVerticalNav && state === SHOW_TOOLTIP

  // Stay on the old nav. No reload needed — we're already there.
  const chooseOldNav = () => setState(SHOW_TOOLTIP)

  // Opt in to the vertical nav. Persist before reloading so the new-nav
  // tooltip shows once the page comes back on the vertical nav.
  const enableVerticalNav = async () => {
    await request(OPT_IN_URL, { method: "post" })
    setState(SHOW_TOOLTIP)
    window.location.reload()
  }

  // Switch back to the old nav. The opt-in choice has run its course.
  const switchToOldNav = async () => {
    await request(OPT_IN_URL, { method: "delete" })
    setState(DONE)
    window.location.reload()
  }

  // Only meaningful while a tooltip is pending. Read fresh state and guard
  // against clobbering the initial `null`: a creator who can't opt in yet still
  // opens these menus normally, and marking them "done" would hide the greeting
  // modal forever once the opt-in is enabled for them.
  const dismissTooltip = () => {
    if (readState() === SHOW_TOOLTIP) setState(DONE)
  }

  return {
    optInEnabled,
    shouldShowModal,
    shouldShowOldNavTooltip,
    shouldShowNewNavTooltip,
    chooseOldNav,
    enableVerticalNav,
    switchToOldNav,
    dismissTooltip
  }
}
